import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Alert, Form, FormInstance, Input, InputNumber, Space, TableColumnsType } from 'antd';

import {
  Crud,
  CrudMode,
  SelectsOptions,
  IconButton,
  getSelectsOptions,
  notification,
  renderCrudFormSortableGroup,
  rejectionError,
  transformCrudFormErrors,
} from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudFormGetDraggableRow, CrudProps, RenderFormItem, ValidationError } from '@ohpen/enso-ui';
import {
  FeeDefinition,
  UsageResponse,
  FeeDefinitionCrudRequest,
  FeeBandRequest,
  FeeType,
  FeeCalculationType,
  FeeBasedOn,
  FeeValidationErrors,
  FeeDefinitionsCreateChangeRequestResponse,
  ChangeRequestOfFeeDefinitionCrudRequest,
  PaymentStage,
  PaymentMethod,
  feeDefinitionsCheckUsage,
  feeDefinitionsCreateChangeRequest,
} from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';
import {
  crudToActionMap,
  getApiErrorMessage,
  changeRequestNotificationMessage,
  changeRequestButtonMessage,
  formatDecimals,
  getDecimalsByField,
} from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { FormGeneralErrors, ReadonlyBandsTable } from '@pmp/components';

import { getFeesFormSchema } from '../formConfig';
import { bands } from '../tableConfig';
import styles from '../styles.module.css';

const BAND_DECIMALS = getDecimalsByField(bands);

export const FeesCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<FeeDefinition>): ReactElement => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [isUsed, setIsUsed] = useState<boolean | null>(null);
    const [generalErrors, setGeneralErrors] = useState<string[]>([]);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    useEffect(() => {
      if (isUsed !== null) return;
      if (mode !== CrudMode.EDIT) {
        setIsUsed(false);
        setOpen(true);
        return;
      }
      const { feeId } = record || {};

      if (!feeId) {
        notification().error({ title: t('global.errors.unexpected') });
        handleOnClose();
        return;
      }
      feeDefinitionsCheckUsage({ path: { feeId } })
        .then(({ data: { isUsed } }: { data: UsageResponse }) => {
          setIsUsed(isUsed);
          setOpen(true);
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
          handleOnClose();
        });
    }, [mode, record, isUsed, t, handleOnClose]);

    const onConfirm: (formData: FeeDefinitionCrudRequest) => Promise<ValidationError[] | void> = useCallback(
      data => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));
        setGeneralErrors([]);

        const body: ChangeRequestOfFeeDefinitionCrudRequest = {
          data: { ...data, bands: data.bands || [{} as FeeBandRequest] },
          actionType: crudToActionMap[mode],
        };
        return feeDefinitionsCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: FeeDefinitionsCreateChangeRequestResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              const { generalErrors: formErrors, ...fieldErrors }: FeeValidationErrors = errors ?? {};
              setGeneralErrors(formErrors ?? []);

              return transformCrudFormErrors(fieldErrors);
            }
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes],
    );

    const getRow: CrudFormGetDraggableRow = useCallback(
      (index, onDeleteRow) => {
        const disabled = mode === CrudMode.VIEW || !!isUsed;
        const calculationType = ref.current?.form.getFieldValue('calculationType');
        const required = calculationType === 'BANDEDFIXEDAMOUNT' || calculationType?.key === 'BANDEDFIXEDAMOUNT';

        return (
          <div className={styles.feeBandRow}>
            <div className={styles.feeBandCell}>
              <Space.Compact block>
                <Input className={styles.currencyPrefix} defaultValue="£" disabled />
                <Form.Item name={['bands', index as number, 'baseBottomAmount']} noStyle required={required}>
                  <InputNumber disabled={disabled} precision={0} placeholder="" />
                </Form.Item>
              </Space.Compact>
            </div>
            <div className={styles.feeBandCell}>
              <Space.Compact block>
                <Input className={styles.currencyPrefix} defaultValue="£" disabled />
                <Form.Item name={['bands', index as number, 'baseTopAmount']} noStyle required={required}>
                  <InputNumber disabled={disabled} precision={0} placeholder="" />
                </Form.Item>
              </Space.Compact>
            </div>
            <div className={styles.feeBandCell}>
              <Space.Compact block>
                <Input className={styles.currencyPrefix} defaultValue="£" disabled />
                <Form.Item name={['bands', index as number, 'amount']} noStyle required={required}>
                  <InputNumber disabled={disabled} precision={BAND_DECIMALS.amount} placeholder="" />
                </Form.Item>
              </Space.Compact>
            </div>
            <div className={styles.feeBandDeleteCell}>
              <IconButton icon={<DeleteOutlined />} onClick={() => onDeleteRow?.(index)} disabled={disabled || !onDeleteRow} />
            </div>
          </div>
        );
      },
      [isUsed, mode],
    );

    const showFeeInUseAlert = mode === CrudMode.EDIT && !!isUsed;

    const renderAlert: RenderFormItem<{ index: number }> = useCallback(() => {
      if (!generalErrors.length && !showFeeInUseAlert) return null;

      return (
        <>
          <FormGeneralErrors errors={generalErrors} />
          {showFeeInUseAlert && <Alert message={t('pmp.fees.labels.feeInUseMessage')} type="warning" showIcon />}
        </>
      );
    }, [showFeeInUseAlert, t, generalErrors]);

    const renderFeeBands: RenderFormItem<{ index: number }> = useCallback(
      (schema, config, form) => {
        const readonly = mode === CrudMode.VIEW;
        const disabled = readonly || !!isUsed;
        const minLength = 1;
        const bands = ((form as FormInstance | undefined)?.getFieldValue(['bands']) as FeeBandRequest[] | undefined) ?? [];

        // A band can only start one above the previous top, or the backend refuses the fee as a gap or an overlap.
        const getNewBand = (currentBands: Array<FeeBandRequest>): FeeBandRequest => {
          const previousTopAmount = currentBands[currentBands.length - 1]?.baseTopAmount;
          return typeof previousTopAmount === 'number' ? { baseBottomAmount: previousTopAmount + 1 } : {};
        };

        if (readonly) {
          const renderAmount = (decimals?: number) => (value?: number | null) =>
            value == null ? '-' : `£${formatDecimals(value, decimals)}`;
          const columns: TableColumnsType<FeeBandRequest> = [
            {
              title: t('pmp.fees.labels.baseBottomAmount'),
              dataIndex: 'baseBottomAmount',
              render: renderAmount(BAND_DECIMALS.baseBottomAmount),
            },
            { title: t('pmp.fees.labels.baseTopAmount'), dataIndex: 'baseTopAmount', render: renderAmount(BAND_DECIMALS.baseTopAmount) },
            { title: t('pmp.fees.labels.amount'), dataIndex: 'amount', render: renderAmount(BAND_DECIMALS.amount) },
          ];

          return <ReadonlyBandsTable<FeeBandRequest> columns={columns} dataSource={bands} />;
        }

        return (
          <>
            <div className={styles.feeBandsTitles}>
              <div className={`${styles.feeBandCell} ${styles.bandHeading}`}>{t('pmp.fees.labels.baseBottomAmount')}</div>
              <div className={`${styles.feeBandCell} ${styles.bandHeading}`}>{t('pmp.fees.labels.baseTopAmount')}</div>
              <div className={`${styles.feeBandCell} ${styles.bandHeading}`}>{t('pmp.fees.labels.amount')}</div>
              <div className={styles.feeBandDeleteCell} />
            </div>
            {renderCrudFormSortableGroup({
              schema,
              config,
              form,
              addButton: { label: t('pmp.fees.labels.addBand') },
              getRow,
              disabled,
              minLength,
              setDisableSave: ref.current?.setDisableSave,
              getNewRow: getNewBand,
            })}
          </>
        );
      },
      [getRow, isUsed, mode, t],
    );

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.fees.labels.fee') }),
        [CrudMode.CREATE]: t('enso.labels.createX', { x: t('pmp.fees.labels.fee') }),
        [CrudMode.EDIT]: t('enso.labels.editX', { x: t('pmp.fees.labels.fee') }),
      };
      return titles[mode];
    }, [mode, t]);

    const initialValues: FeeDefinitionCrudRequest | null = useMemo(() => {
      if (!record) return null;
      const { feeType, calculationType, basedOn, bands, paymentStages, paymentMethods, ...props } = record;

      return {
        ...props,
        feeType: feeType?.key as FeeType | undefined,
        calculationType: calculationType?.key as FeeCalculationType | undefined,
        basedOn: basedOn?.key as FeeBasedOn | undefined,
        bands: bands as Array<FeeBandRequest> | null,
        paymentStages: paymentStages?.map(stage => stage.key as PaymentStage) || null,
        paymentMethods: paymentMethods?.map(method => method.key as PaymentMethod) || null,
      };
    }, [record]);

    const crudProps: CrudProps<FeeDefinitionCrudRequest> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return {
        ref,
        open,
        schema: getFeesFormSchema(selectsOptions, renderFeeBands, renderAlert, mode, isUsed),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('enso.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        readonly: mode === CrudMode.VIEW,
        width: 'm',
        onClose: handleOnClose,
        onConfirm,
      };
    }, [
      valueLists,
      open,
      renderFeeBands,
      renderAlert,
      mode,
      isUsed,
      title,
      t,
      initialValues,
      handleOnClose,
      onConfirm,
      enableFourEyes,
    ]);

    return <Crud {...crudProps} />;
  },
);
